import React, { useState } from 'react';
import { CircleArrowRight, X } from 'lucide-react';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';

// ─── Static data ──────────────────────────────────────────────────────────────

// Each category keeps its own recipient list, independent of the others.
const CATEGORIES = [
    { key: 'failureEmails', label: 'Failure', colorClass: 'failure', errorKey: 'notificationEmailsFailure' },
    { key: 'successEmails', label: 'Success', colorClass: 'success', errorKey: 'notificationEmailsSuccess' },
    { key: 'successWithChangeEmails', label: 'Success with change', colorClass: 'success-with-change', errorKey: 'notificationEmailsSuccessWithChange' },
];

// Maximum number of notification email addresses a single category can have.
export const MAX_NOTIFICATION_EMAILS = 50;

// ─── Category block ───────────────────────────────────────────────────────────

const NotificationCategory = ({ label, colorClass, emails, draft, onDraftChange, onCommit, onRemove, errorMessage }) => {
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); onCommit(); }
    };

    return (
        <div className={`notification-category notification-category--${colorClass}`}>
            <span className="notification-category-label">{label}</span>
            <div className="notification-category-body">
                <div className="notification-category-input-row">
                    <div className="notification-category-input">
                        <input
                            type="text"
                            value={draft}
                            onChange={(e) => onDraftChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className={errorMessage ? 'error' : ''}
                        />
                        <button type="button" className="icon-in-input" onClick={onCommit}>
                            <CircleArrowRight style={{ width: '1.042vw', height: '1.042vw' }} color="#2563eb" />
                        </button>
                    </div>
                    <span className={`notification-category-counter${emails.length >= MAX_NOTIFICATION_EMAILS ? ' limit-reached' : ''}`}>
                        {emails.length}/{MAX_NOTIFICATION_EMAILS}
                    </span>
                </div>
                <ErrorMessage message={errorMessage} />
                {emails.length > 0 ? (
                    <div className="chips-container">
                        {emails.map((email, index) => (
                            <div key={email} className="chip">
                                <span>{email}</span>
                                <button type="button" onClick={() => onRemove(index)}>
                                    <X style={{ width: '0.729vw', height: '0.729vw' }} />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="notification-category-empty">No recipient yet</p>
                )}
            </div>
        </div>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────

const NotificationsSection = ({
    notifications,
    errors,
    onAddEmails,
    onRemoveEmail,
    onEmailInputChange,
}) => {
    // Transient input text lives here, one draft per category; committed email
    // lists live in the parent.
    const [drafts, setDrafts] = useState({ failureEmails: '', successEmails: '', successWithChangeEmails: '' });

    const commit = (key) => {
        const value = drafts[key];
        if (!value.trim()) return;
        // onAddEmails returns true once the input passes format validation, so the
        // field is only cleared when the entry was accepted.
        if (onAddEmails(key, value)) {
            setDrafts(prev => ({ ...prev, [key]: '' }));
        }
    };

    return (
        <div className="notifications-section">
            <ErrorMessage message={errors.notificationsRequired} />
            {CATEGORIES.map(({ key, label, colorClass, errorKey }) => (
                <NotificationCategory
                    key={key}
                    label={label}
                    colorClass={colorClass}
                    emails={notifications[key]}
                    draft={drafts[key]}
                    onDraftChange={(value) => {
                        setDrafts(prev => ({ ...prev, [key]: value }));
                        onEmailInputChange?.(key);
                    }}
                    onCommit={() => commit(key)}
                    onRemove={(index) => onRemoveEmail(key, index)}
                    errorMessage={errors[errorKey]}
                />
            ))}
        </div>
    );
};

export default NotificationsSection;
